import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHeroDecor from '@/components/feature/PageHeroDecor';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';
import { useSEO } from '@/hooks/useSEO';

const phases = [
  {
    num: '01',
    en: 'Discovery',
    title: '意味の発掘',
    duration: '2〜3週間',
    desc: '組織の内側にある「意味」を掘り起こすフェーズです。答えを持ち込まず、対話の中から本質を見つけます。',
    activities: [
      { label: 'キックオフミーティング', body: '担当者・経営層と現状・課題・目指す姿を共有します。プロジェクトの方向性と進め方を合意します。' },
      { label: 'インタビュー調査', body: 'スタッフ・利用者・関係者へのインタビューを実施。組織の外から見えている価値と、内側にある誇りを収集します。' },
      { label: '現場観察・資料分析', body: '既存の発信物・報告書・広報資料を分析し、これまでの言葉の傾向と課題を整理します。' },
    ],
    output: '現状分析レポート・課題整理シート',
  },
  {
    num: '02',
    en: 'Definition',
    title: '意味の言語化',
    duration: '2〜4週間',
    desc: '発掘した素材を「社会に届く言葉」に変換するフェーズです。組織の核となるコンセプトを設計します。',
    activities: [
      { label: 'コンセプトワーク', body: '組織の存在意義・強み・独自性を言語化します。複数の方向性を提示し、対話を通じて絞り込みます。' },
      { label: 'ワークショップ', body: 'スタッフ参加型のワークショップで、言葉を組織全体のものにします。外から押し付けるのではなく、内側から育てます。' },
      { label: 'コアメッセージ策定', body: 'ミッション・ビジョン・バリュー、キャッチコピー、ブランドストーリーなど、発信の核となる言葉を確定します。' },
    ],
    output: 'コンセプトブック・コアメッセージ一式',
  },
  {
    num: '03',
    en: 'Architecture',
    title: '構造設計',
    duration: '2〜3週間',
    desc: '言語化された意味を、どのように社会に届けるかの構造を設計するフェーズです。媒体と情報の全体像を整理します。',
    activities: [
      { label: 'コミュニケーション設計', body: '誰に・何を・どの順序で・どの媒体で伝えるかを設計します。ターゲットごとの接点と情報の優先順位を整理します。' },
      { label: '媒体計画', body: 'パンフレット・Webサイト・映像・SNSなど、最適な媒体の組み合わせを提案します。予算・優先度・効果を考慮して選定します。' },
      { label: '制作ディレクション計画', body: '各媒体の制作スケジュール・担当・外部パートナーとの連携方針を策定します。' },
    ],
    output: 'コミュニケーション設計書・媒体計画書',
  },
  {
    num: '04',
    en: 'Creation',
    title: '制作・実装',
    duration: '1〜2ヶ月',
    desc: '設計した構造に基づき、各種コンテンツや媒体を制作・実装するフェーズです。品質と一貫性を担保します。',
    activities: [
      { label: 'コンテンツ制作', body: 'パンフレット・Webサイト・映像・写真・年次報告書など、設計に基づいた制作物を制作します。外部クリエイターとの連携もディレクションします。' },
      { label: '編集・ライティング', body: '専門性を持ちながら読みやすく、共感を生む文章を設計・執筆します。コアメッセージとの一貫性を全媒体で担保します。' },
      { label: 'レビュー・修正', body: '制作物の確認・修正を繰り返し、品質を高めます。組織内部での確認プロセスも設計し、スムーズな承認を支援します。' },
    ],
    output: '各種制作物（パンフレット・Webサイト・映像など）',
  },
  {
    num: '05',
    en: 'Circulation',
    title: '循環の育成',
    duration: '継続的',
    desc: '発信が循環し始めたら、その構造を組織内部に定着させるフェーズです。外部依存から自走へ移行します。',
    activities: [
      { label: '発信体制の構築', body: '担当者の育成、ワークフローの設計、ツールの選定など、組織内部で継続できる発信の仕組みをつくります。' },
      { label: '定期レビュー', body: '月次・四半期ごとに発信の効果と課題を振り返ります。数字だけでなく、「意味が届いているか」という視点で評価します。' },
      { label: '継続的な伴走', body: '組織の変化・成長に合わせて、言葉と構造をアップデートします。長期的なパートナーとして関わり続けます。' },
    ],
    output: '発信体制マニュアル・定期レビューレポート',
  },
];

const principles = [
  {
    en: 'Dialogue over Delivery',
    title: '納品より、対話。',
    body: '私たちは「答えを持ち込む」コンサルタントではありません。組織の内側にある意味を、対話を通じて一緒に掘り起こします。プロセスそのものが、組織の力になります。',
  },
  {
    en: 'Inside Out',
    title: '外から押し付けない。',
    body: 'コンセプトやメッセージは、外部の視点で「つくる」のではなく、組織の内側から「育てる」ものです。スタッフが誇りを持って語れる言葉でなければ、社会には届きません。',
  },
  {
    en: 'Long-term Partnership',
    title: '一度きりではなく、長期的に。',
    body: '組織の意味は、一度言語化すれば終わりではありません。組織が成長し、社会が変化するにつれて、言葉も構造も更新が必要です。私たちは長期的なパートナーとして関わります。',
  },
];

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function PrinciplesGrid({ items }: { items: typeof principles }) {
  const { ref, visibleCount } = useStaggerAnimation(items.length);
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="grid grid-cols-1 md:grid-cols-3 gap-px bg-divider border border-divider">
      {items.map((p, idx) => (
        <div
          key={p.en}
          className="bg-warm-light p-10 md:p-12 flex flex-col gap-5"
          style={{
            opacity: visibleCount > idx ? 1 : 0,
            transform: visibleCount > idx ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}
        >
          <span className="font-garamond text-xs italic text-muted-light">{p.en}</span>
          <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-xl leading-snug">{p.title}</h2>
          <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{p.body}</p>
        </div>
      ))}
    </div>
  );
}

export default function SusumerkataPage() {
  useSEO({
    title: '進め方・プロセス | メセナインターコム株式会社',
    description: 'メセナインターコム株式会社のプロジェクト進め方。意味の発掘・言語化・構造設計・制作実装・循環の育成の5フェーズで、医療・福祉・教育・地域・文化の組織の意味設計を進めます。対話を重視した独自のプロセス。',
    keywords: '進め方,プロセス,意味の発掘,言語化,構造設計,コンサルティング,医療,福祉,教育,地域,文化',
    ogTitle: '進め方・プロセス | メセナインターコム株式会社',
    ogDescription: '意味の発掘から循環の育成まで、5つのフェーズで進みます。対話の中から、その組織だけの言葉と構造を設計します。',
    ogUrl: 'https://mecena-intercom.co.jp/susumerkata',
    canonical: 'https://mecena-intercom.co.jp/susumerkata',
  });

  const [activePhase, setActivePhase] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-28 md:pt-52 md:pb-40 bg-warm-white relative overflow-hidden">
          <PageHeroDecor variant="teal" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div
                ref={heroRef}
                className="md:col-span-8 md:col-start-3"
                style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s' }}
              >
                <div className="flex items-center gap-4 mb-10">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Process</span>
                </div>
                <h1 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-4xl md:text-5xl mb-12">
                  進め方
                </h1>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-xl">
                  意味の発掘から循環の育成まで、5つのフェーズで進みます。<br />
                  すべてのプロジェクトに同じ答えを持ち込まず、<br />
                  対話の中から、その組織だけの言葉と構造を設計します。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Principles */}
        <section className="texture-stance py-20 md:py-28 bg-warm-light relative overflow-hidden">
          {/* 「3つの原則・対話・長期」テーマ装飾 */}
          <div className="absolute top-0 right-0 w-28 h-28 lg:w-52 lg:h-52 pointer-events-none overflow-hidden">
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(74,158,142,0.20) 1.5px, transparent 1.5px)', backgroundSize: '16px 16px' }} />
          </div>
          <div className="absolute -bottom-10 -left-10 w-[160px] h-[160px] lg:w-[300px] lg:h-[300px] rounded-full border border-accent-teal/14 pointer-events-none" />
          <div className="absolute top-8 right-8 lg:top-14 lg:right-16 w-6 h-6 lg:w-12 lg:h-12 border border-accent-teal/30 rotate-45 pointer-events-none" />
          <div className="absolute top-12 right-12 lg:top-20 lg:right-24 w-3 h-3 lg:w-6 lg:h-6 border border-accent-amber/35 rotate-45 pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <FadeIn delay={0}>
              <div className="flex items-center gap-4 mb-14">
                <span className="block w-6 h-px bg-accent-teal" />
                <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Our Principles</span>
              </div>
            </FadeIn>
            <PrinciplesGrid items={principles} />
          </div>
        </section>


        {/* Phase detail */}
        <section className="py-20 md:py-32 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn delay={0}>
              <div className="flex items-center gap-4 mb-14">
                <span className="block w-6 h-px bg-accent-teal" />
                <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">5 Phases</span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1}>
              {/* Phase group labels — 5等分に合わせる */}
              <div className="flex gap-0 mb-0">
                {/* 01 */}
                <div className="flex-1 flex items-center justify-end pr-3 py-2 border border-b-0 border-r-0 border-divider bg-warm-light/60" />
                {/* 02 — 戦略フェーズラベルを右端に */}
                <div className="flex-1 flex items-center justify-end pr-3 py-2 border border-b-0 border-r-0 border-divider bg-warm-light/60">
                  <span className="text-[10px] font-sans-jp text-muted-brown tracking-widest-jp border border-muted-light/40 px-2 py-0.5 whitespace-nowrap">
                    戦略フェーズ
                  </span>
                </div>
                {/* 03 */}
                <div className="flex-1 flex items-center justify-end pr-3 py-2 border border-b-0 border-r-0 border-divider bg-warm-light/60" />
                {/* 04 — 制作フェーズラベルを右端に */}
                <div className="flex-1 flex items-center justify-end pr-3 py-2 border border-b-0 border-r-0 border-divider bg-accent-teal/5">
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp border border-accent-teal/50 px-2 py-0.5 whitespace-nowrap">
                    制作フェーズ
                  </span>
                </div>
                {/* 05 */}
                <div className="flex-1 flex items-center justify-end pr-3 py-2 border border-b-0 border-divider bg-warm-light/60" />
              </div>

              {/* Phase tabs */}
              <div className="flex flex-wrap gap-0 border border-divider mb-0">
                {phases.map((phase, idx) => (
                  <button
                    key={phase.num}
                    onClick={() => setActivePhase(idx)}
                    className={`flex-1 min-w-[120px] flex flex-col items-center gap-1.5 py-5 px-4 border-r border-divider last:border-r-0 transition-colors duration-200 cursor-pointer ${activePhase === idx ? 'bg-charcoal' : 'bg-warm-white hover:bg-warm-light'}`}
                  >
                    <span className={`font-garamond text-xs italic transition-colors duration-200 ${activePhase === idx ? 'text-accent-teal' : 'text-muted-light'}`}>
                      {phase.en}
                    </span>
                    <span className={`font-garamond text-2xl font-light transition-colors duration-200 ${activePhase === idx ? 'text-warm-white' : 'text-divider'}`}>
                      {phase.num}
                    </span>
                    <span className={`font-sans-jp text-xs tracking-wide-jp transition-colors duration-200 ${activePhase === idx ? 'text-warm-white' : 'text-muted-brown'}`}>
                      {phase.title}
                    </span>
                  </button>
                ))}
              </div>

              {/* Phase content */}
              {phases.map((phase, idx) => (
                <div
                  key={phase.num}
                  className={`border border-t-0 border-divider transition-all duration-300 ${activePhase === idx ? 'block' : 'hidden'}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12">
                    {/* Left */}
                    <div className="md:col-span-4 p-10 md:p-14 border-b md:border-b-0 md:border-r border-divider flex flex-col gap-6">
                      <div>
                        <span className="font-garamond text-xs italic text-muted-light block mb-2">{phase.en}</span>
                        <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl md:text-3xl mb-4">
                          {phase.title}
                        </h3>
                        <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{phase.desc}</p>
                      </div>
                      <div className="pt-4 border-t border-divider">
                        <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-3">Duration</p>
                        <p className="font-garamond text-lg font-light text-charcoal">{phase.duration}</p>
                      </div>
                      <div className="pt-4 border-t border-divider">
                        <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-3">Output</p>
                        <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{phase.output}</p>
                      </div>
                    </div>

                    {/* Right */}
                    <div className="md:col-span-8 p-10 md:p-14">
                      <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-8">Activities</p>
                      <div className="space-y-0 border-t border-divider">
                        {phase.activities.map((act, ai) => (
                          <div key={ai} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 border-b border-divider">
                            <div className="md:col-span-4 flex items-start gap-3">
                              <span className="block w-1 h-1 rounded-full bg-accent-teal flex-shrink-0 mt-2" />
                              <h4 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-base leading-snug">{act.label}</h4>
                            </div>
                            <div className="md:col-span-8">
                              <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{act.body}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </FadeIn>
          </div>
        </section>

        {/* Timeline overview */}
        <section className="texture-process py-20 md:py-28 bg-warm-light relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn delay={0}>
              <div className="flex items-center gap-4 mb-14">
                <span className="block w-6 h-px bg-accent-teal" />
                <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Timeline</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
                <div className="md:col-span-6">
                  <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl">
                    標準的なプロジェクトの流れ
                  </h2>
                </div>
                <div className="md:col-span-6 flex items-end">
                  <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                    プロジェクトの内容・規模によって期間は変わります。<br />
                    以下は制作物を含む標準的なプロジェクトの目安です。
                  </p>
                </div>
              </div>

              {/* Timeline bar */}
              <div className="overflow-x-auto">
                <div className="min-w-[700px]">
                  {/* 月ラベル行 */}
                  <div className="flex mb-1">
                    {/* フェーズ名列 */}
                    <div className="w-32 flex-shrink-0" />
                    {/* 月グリッド */}
                    <div className="flex-1 flex">
                      {['1ヶ月目', '2ヶ月目', '3ヶ月目', '4ヶ月目', '継続'].map((m) => (
                        <div key={m} className="flex-1 text-center pb-2 border-b border-divider">
                          <span className="font-sans-jp text-[10px] text-muted-light tracking-wide-jp">{m}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* フェーズ行 */}
                  {[
                    {
                      num: '01',
                      label: '意味の発掘',
                      // offset・span は 5ヶ月 = 100% 基準
                      offset: 0,
                      span: 0.7,
                      phase: 'strategy',
                    },
                    {
                      num: '02',
                      label: '意味の言語化',
                      offset: 0.55,
                      span: 0.9,
                      phase: 'strategy',
                    },
                    {
                      num: '03',
                      label: '構造設計',
                      offset: 1.35,
                      span: 0.65,
                      phase: 'strategy',
                    },
                    {
                      num: '04',
                      label: '制作・実装',
                      offset: 1.9,
                      span: 1.6,
                      phase: 'creation',
                    },
                    {
                      num: '05',
                      label: '循環の育成',
                      offset: 3.6,
                      span: 1.4,
                      phase: 'circulation',
                    },
                  ].map((row) => {
                    const isStrategy = row.phase === 'strategy';
                    const isCreation = row.phase === 'creation';
                    const barBg = isStrategy
                      ? 'bg-[#5a7a6e]/15'
                      : isCreation
                      ? 'bg-[#5a7a6e]/35'
                      : 'bg-[#5a7a6e]/8';
                    const barBorder = isStrategy
                      ? 'border-[#5a7a6e]/40'
                      : isCreation
                      ? 'border-[#5a7a6e]/60'
                      : 'border-[#5a7a6e]/25';
                    const numColor = isStrategy
                      ? 'text-[#5a7a6e]/70'
                      : isCreation
                      ? 'text-[#5a7a6e]'
                      : 'text-[#5a7a6e]/50';
                    const labelColor = isCreation ? 'text-charcoal font-medium' : 'text-charcoal/80';

                    return (
                      <div key={row.num} className="flex items-center h-11 group">
                        {/* フェーズ番号・名前 */}
                        <div className="w-32 flex-shrink-0 flex items-center gap-2 pr-4">
                          <span className="font-garamond text-xs italic text-muted-light">{row.num}</span>
                          <span className="font-sans-jp text-xs text-muted-brown truncate">{row.label}</span>
                        </div>
                        {/* グリッド背景 + バー */}
                        <div className="flex-1 relative h-full flex items-center">
                          {/* 縦グリッド線 */}
                          <div className="absolute inset-0 flex pointer-events-none">
                            {[0, 1, 2, 3, 4].map((i) => (
                              <div key={i} className="flex-1 border-r border-divider/60 last:border-r-0" />
                            ))}
                          </div>
                          {/* バー本体 */}
                          <div
                            className="absolute inset-y-1.5"
                            style={{
                              left: `${(row.offset / 5) * 100}%`,
                              width: `${(row.span / 5) * 100}%`,
                            }}
                          >
                            <div className={`w-full h-full border ${barBg} ${barBorder} flex items-center px-2.5 gap-1.5`}>
                              <span className={`font-garamond text-[10px] italic flex-shrink-0 ${numColor}`}>{row.num}</span>
                              <span className={`font-sans-jp text-[11px] whitespace-nowrap ${labelColor}`}>{row.label}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* 凡例 */}
                  <div className="flex items-center gap-6 mt-6 pt-5 border-t border-divider">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-3 border border-[#5a7a6e]/40 bg-[#5a7a6e]/15" />
                      <span className="font-sans-jp text-[10px] text-muted-light tracking-wide-jp">戦略フェーズ</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-3 border border-[#5a7a6e]/60 bg-[#5a7a6e]/35" />
                      <span className="font-sans-jp text-[10px] text-muted-light tracking-wide-jp">制作フェーズ</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-3 border border-[#5a7a6e]/25 bg-[#5a7a6e]/8" />
                      <span className="font-sans-jp text-[10px] text-muted-light tracking-wide-jp">継続フェーズ</span>
                    </div>
                    <span className="font-sans-jp text-[10px] text-muted-light ml-auto">※ 規模・内容により変動します</span>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* FAQ teaser */}
        <section className="py-20 md:py-28 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn delay={0}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
                <div className="md:col-span-6">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="block w-6 h-px bg-accent-teal" />
                    <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Common Questions</span>
                  </div>
                  <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl">
                    よくある疑問
                  </h2>
                </div>
              </div>
              <div className="space-y-0 border-t border-divider mb-12">
                {[
                  { q: 'プロジェクトの途中から依頼することはできますか？', a: 'はい、可能です。すでに制作が進んでいる場合でも、意味設計の部分から入ることができます。まずは現状を共有いただき、どこから支援できるかを一緒に整理しましょう。' },
                  { q: '社内に担当者がいない場合でも進められますか？', a: 'はい、担当者の育成も含めて支援します。プロジェクトを通じて、スタッフが自ら語れるようになることを目指します。外部依存ではなく、組織の力を育てることが私たちの目標です。' },
                  { q: '一部のフェーズだけ依頼することはできますか？', a: 'はい、部分的な支援も可能です。ただし、意味設計なしに制作だけを依頼された場合、私たちの強みが十分に発揮できないことがあります。まずはご相談ください。' },
                ].map((item, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-12 py-8 border-b border-divider">
                    <div className="md:col-span-5 flex items-start gap-4">
                      <span className="font-garamond text-xs italic text-muted-light flex-shrink-0 mt-0.5">Q</span>
                      <p className="font-serif-jp font-light text-charcoal tracking-normal-jp text-base leading-snug">{item.q}</p>
                    </div>
                    <div className="md:col-span-7 flex items-start gap-4">
                      <span className="font-garamond text-xs italic text-accent-teal flex-shrink-0 mt-0.5">A</span>
                      <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-sm font-sans-jp text-muted-brown hover:text-charcoal transition-colors duration-200 tracking-wide-jp"
              >
                すべてのFAQを見る →
              </Link>
            </FadeIn>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 bg-warm-light border-t border-divider">
          <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl mb-3">
                まずは30分、現状を一緒に整理しましょう。
              </h3>
              <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                課題が明確でなくても構いません。対話の中から始められます。
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-charcoal text-sm font-sans-jp text-charcoal tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap"
              >
                事例を見る →
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap"
              >
                相談してみる
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
